/** Pure text and time helpers shared by the sample client and the screens. */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** 12 -> "12m", 180 -> "3h", 3000 -> "2d". Never negative. */
export function ageLabel(minutes: number): string {
  const m = Math.max(0, Math.round(minutes))
  if (m < 1) {
    return 'now'
  }
  if (m < 60) {
    return `${m}m`
  }
  const hours = Math.round(m / 60)
  if (hours < 24) {
    return `${hours}h`
  }
  return `${Math.round(hours / 24)}d`
}

export function secondsLabel(seconds: number): string {
  const s = Math.max(0, Math.round(seconds))
  if (s < 60) {
    return `${s}s`
  }
  return `${Math.round(s / 60)}m`
}

export function hoursLabel(hours: number): string {
  if (hours < 1) {
    return `${Math.max(1, Math.round(hours * 60))}m`
  }
  if (hours < 48) {
    return `${Math.round(hours * 10) / 10}h`
  }
  return `${Math.round(hours / 24)}d`
}

export function minutesBetween(fromIso: string, toIso: string): number {
  return (Date.parse(toIso) - Date.parse(fromIso)) / 60_000
}

export function monthYear(iso: string | null | undefined): string {
  if (!iso) {
    return ''
  }
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) {
    return ''
  }
  return `${MONTHS[date.getMonth()]} ${date.getFullYear()}`
}

export function shortDate(iso: string | null | undefined): string {
  if (!iso) {
    return ''
  }
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) {
    return ''
  }
  return `${MONTHS[date.getMonth()]} ${date.getDate()}`
}

function clock(date: Date): string {
  const hours = date.getHours()
  const hour12 = hours % 12 || 12
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return `${hour12}:${minutes} ${hours >= 12 ? 'PM' : 'AM'}`
}

/** "Today, 9:04 AM", "Yesterday, 4:10 PM", or "Sep 14, 9:04 AM". Against the sample clock. */
export function whenLabel(iso: string, nowIso: string): string {
  const date = new Date(iso)
  const now = new Date(nowIso)
  if (Number.isNaN(date.getTime())) {
    return ''
  }
  const startOfDay = (value: Date) =>
    new Date(value.getFullYear(), value.getMonth(), value.getDate()).getTime()
  const dayDiff = Math.round((startOfDay(now) - startOfDay(date)) / 86_400_000)
  if (dayDiff === 0) {
    return `Today, ${clock(date)}`
  }
  if (dayDiff === 1) {
    return `Yesterday, ${clock(date)}`
  }
  return `${MONTHS[date.getMonth()]} ${date.getDate()}, ${clock(date)}`
}

export function tenureLabel(sinceIso: string | null | undefined, nowIso: string): string | null {
  if (!sinceIso) {
    return null
  }
  const days = Math.floor((Date.parse(nowIso) - Date.parse(sinceIso)) / 86_400_000)
  if (days < 0) {
    return null
  }
  if (days < 30) {
    return days <= 1 ? 'New' : `${days} days`
  }
  const months = Math.round(days / 30.4)
  return `${months} month${months === 1 ? '' : 's'}`
}

export function initials(name: string): string {
  const letters = name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase() || '')
    .join('')
  return letters || '?'
}

export function stripBold(text: string): string {
  return text.replace(/\*\*(.+?)\*\*/g, '$1')
}

/** First sentence, without the bold marks. "**Money leaving.** The AI never..." -> "Money leaving". */
export function leadSentence(text: string): string {
  const plain = stripBold(text).trim()
  const end = plain.search(/[.!?](\s|$)/)
  const first = end === -1 ? plain : plain.slice(0, end)
  return first.trim()
}

export function snippet(text: string, max = 56): string {
  const flat = text.replace(/\s+/g, ' ').trim()
  if (flat.length <= max) {
    return flat
  }
  const cut = flat.slice(0, max)
  const space = cut.lastIndexOf(' ')
  return `${(space > 24 ? cut.slice(0, space) : cut).replace(/[,;:.\s]+$/, '')}…`
}

/** The part of a message worth showing in a timeline: the question if there is one, else the first sentence. */
export function gist(text: string, max = 52): string {
  const flat = text.replace(/\s+/g, ' ').trim()
  const sentences = flat.split(/(?<=[.!?])\s+/).filter(Boolean)
  const question = sentences.find(sentence => sentence.endsWith('?'))
  const first = sentences.find(sentence => sentence.length > 14) || sentences[0] || flat
  return snippet((question || first).replace(/^(thanks|thank you)( again)?[,.!]?\s+/i, ''), max)
}

export type RichPart = { text: string; kind: 'plain' | 'bold' | 'variable' }

/** Splits `**bold**` and `{{variable}}` so a screen can style each part. */
export function parseRich(text: string): RichPart[] {
  const parts: RichPart[] = []
  const pattern = /\*\*(.+?)\*\*|\{\{\s*([a-z0-9_]+)\s*\}\}/gi
  let last = 0
  let match = pattern.exec(text)
  while (match) {
    if (match.index > last) {
      parts.push({ kind: 'plain', text: text.slice(last, match.index) })
    }
    if (match[1] != null) {
      parts.push({ kind: 'bold', text: match[1] })
    } else {
      parts.push({ kind: 'variable', text: `{{${match[2]}}}` })
    }
    last = match.index + match[0].length
    match = pattern.exec(text)
  }
  if (last < text.length) {
    parts.push({ kind: 'plain', text: text.slice(last) })
  }
  return parts
}

export function pct(part: number, whole: number): number {
  return whole > 0 ? Math.round((part / whole) * 100) : 0
}

export function median(values: number[]): number | null {
  if (values.length === 0) {
    return null
  }
  const sorted = [...values].sort((left, right) => left - right)
  const mid = Math.floor(sorted.length / 2)
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2
}

/** 165.1 -> "about a week", 46.1 -> "2 days", 9.3 -> "9 hours", 0.3 -> "18 minutes". */
export function waitPhrase(hours: number): string {
  if (hours >= 24 * 6) {
    return 'about a week'
  }
  if (hours >= 36) {
    return `${Math.round(hours / 24)} days`
  }
  if (hours >= 22) {
    return 'about a day'
  }
  if (hours >= 1) {
    const rounded = Math.round(hours)
    return `${rounded} hour${rounded === 1 ? '' : 's'}`
  }
  const minutes = Math.max(1, Math.round(hours * 60))
  return `${minutes} minute${minutes === 1 ? '' : 's'}`
}

/** The example message first, then the messages a rule caught. No blanks, no repeats, at most three. */
export function mergeMessages(example: string | null | undefined, caught: string[]): string[] {
  return [example, ...caught]
    .map(text => (text || '').trim())
    .filter(Boolean)
    .filter((text, index, all) => all.indexOf(text) === index)
    .slice(0, 3)
}

/**
 * The notice over a snapshot that is not current: a refresh failed (or could not run) and the server
 * kept the last good copy. It names the date of that copy and does not call it current.
 */
export function staleHubspotNotice(member: {
  hubspotContactId?: string | null
  hubspotFetchedAt?: string | null
  hubspotUnavailableReason?: string | null
}): string | null {
  if (!member.hubspotUnavailableReason || !member.hubspotContactId) {
    return null
  }
  const when = shortDate(member.hubspotFetchedAt)
  return `The last refresh failed. Showing earlier data${when ? ` from ${when}` : ''}.`
}

export function wordCount(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length
}

/**
 * What a conversation row is called: the other person's name first, because the API's `title` is
 * null for an untitled direct chat. The title is the fallback, then `fallback`.
 */
export function conversationLabel(
  row: { memberDisplayName?: string | null; title?: string | null },
  fallback = 'Unknown'
): string {
  return row.memberDisplayName?.trim() || row.title?.trim() || fallback
}
